import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "@tanstack/react-router";

const STORAGE_KEY = "olff.cookie-consent.v1";
export const OPEN_PREFS_EVENT = "olff:open-cookie-preferences";

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

function readConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(consent: CookieConsent) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("olff:cookie-consent-change", { detail: consent }));
}

export function getCookieConsent(): CookieConsent | null {
  return readConsent();
}

/**
 * Loads an optional script only after the user has granted the required category.
 * Use for analytics, marketing pixels, embeds, etc. Never call without checking consent.
 */
export function loadScriptOnConsent(
  category: "analytics" | "marketing",
  loader: () => void,
) {
  if (typeof window === "undefined") return;
  const apply = () => {
    const c = readConsent();
    if (c && c[category]) loader();
  };
  apply();
  window.addEventListener("olff:cookie-consent-change", apply);
}

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Open if no decision yet
    const c = readConsent();
    if (!c) setOpen(true);

    const handler = () => {
      const existing = readConsent();
      setAnalytics(existing?.analytics ?? false);
      setMarketing(existing?.marketing ?? false);
      setShowPrefs(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, handler);
  }, []);

  function save(consent: Omit<CookieConsent, "necessary" | "decidedAt">) {
    writeConsent({
      necessary: true,
      analytics: consent.analytics,
      marketing: consent.marketing,
      decidedAt: new Date().toISOString(),
    });
    setOpen(false);
    setShowPrefs(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-night-deep/95 p-5 shadow-soft backdrop-blur-md md:p-7">
        {!showPrefs ? (
          <>
            <p className="font-mono-cinema text-[10px] text-gold">— Cookies</p>
            <h2 id="cookie-banner-title" className="mt-2 font-display text-xl font-medium text-foreground md:text-2xl">
              Wij respecteren je <span className="italic text-gold">privacy</span>
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground/80">
              Wij gebruiken alleen noodzakelijke cookies om de site te laten werken. Met jouw toestemming
              plaatsen we ook cookies voor statistieken en marketing. Je kunt je keuze later altijd
              wijzigen via de footer.{" "}
              <Link to="/privacybeleid" className="underline hover:text-gold">
                Meer informatie in ons privacybeleid
              </Link>
              .
            </p>
            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              <Button
                type="button"
                variant="hero"
                className="w-full"
                onClick={() => save({ analytics: true, marketing: true })}
              >
                Accepteer alles
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-border bg-night-deep/60 hover:border-gold/50"
                onClick={() => save({ analytics: false, marketing: false })}
              >
                Weiger niet-noodzakelijke cookies
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-border bg-night-deep/60 hover:border-gold/50"
                onClick={() => {
                  const existing = readConsent();
                  setAnalytics(existing?.analytics ?? false);
                  setMarketing(existing?.marketing ?? false);
                  setShowPrefs(true);
                }}
              >
                Voorkeuren instellen
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="font-mono-cinema text-[10px] text-gold">— Cookie-voorkeuren</p>
            <h2 id="cookie-banner-title" className="mt-2 font-display text-xl font-medium text-foreground md:text-2xl">
              Stel je <span className="italic text-gold">voorkeuren</span> in
            </h2>
            <div className="mt-5 space-y-3">
              <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-night-deep/60 p-4">
                <div>
                  <div className="font-medium text-foreground">Noodzakelijk</div>
                  <p className="mt-1 text-sm text-foreground/70">
                    Vereist voor basisfunctionaliteit. Altijd actief.
                  </p>
                </div>
                <Switch checked disabled aria-label="Noodzakelijke cookies (altijd aan)" />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-night-deep/60 p-4">
                <div>
                  <div className="font-medium text-foreground">Statistieken</div>
                  <p className="mt-1 text-sm text-foreground/70">
                    Anoniem inzicht in sitegebruik om de website te verbeteren.
                  </p>
                </div>
                <Switch
                  checked={analytics}
                  onCheckedChange={(v) => setAnalytics(Boolean(v))}
                  aria-label="Statistieken-cookies"
                />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-night-deep/60 p-4">
                <div>
                  <div className="font-medium text-foreground">Marketing</div>
                  <p className="mt-1 text-sm text-foreground/70">
                    Gebruikt voor gepersonaliseerde content en advertenties.
                  </p>
                </div>
                <Switch
                  checked={marketing}
                  onCheckedChange={(v) => setMarketing(Boolean(v))}
                  aria-label="Marketing-cookies"
                />
              </div>
            </div>
            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              <Button
                type="button"
                variant="hero"
                className="w-full"
                onClick={() => save({ analytics: true, marketing: true })}
              >
                Accepteer alles
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-border bg-night-deep/60 hover:border-gold/50"
                onClick={() => save({ analytics: false, marketing: false })}
              >
                Weiger niet-noodzakelijke
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-border bg-night-deep/60 hover:border-gold/50"
                onClick={() => save({ analytics, marketing })}
              >
                Voorkeuren opslaan
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_PREFS_EVENT));
}